import {
  isPerUnitProductVariant,
  isRevShareProductVariant,
  isTieredSubscriptionProductVariant,
  isTiersProductVariant,
  isTiersVolumeProductVariant,
} from '@vy/services';
import type { UsageProductVariantInput } from '@vy/types';
import { isEmptyArray, isSingleItem, raise, uniq, values } from '@vy/utils';
import type { UsagePricingOption } from '../../../../utils/types/usage-pricing-options';
import { USAGE_PRICING_OPTIONS } from '../../../../utils/types/usage-pricing-options';

export function getUsagePricingModelValue(variant: UsageProductVariantInput) {
  if (isRevShareProductVariant(variant)) {
    return USAGE_PRICING_OPTIONS.REVENUE_SHARE;
  }

  if (isPerUnitProductVariant(variant)) {
    return USAGE_PRICING_OPTIONS.PER_UNIT;
  }

  if (isTieredSubscriptionProductVariant(variant)) {
    return USAGE_PRICING_OPTIONS.TIERED_SUBSCRIPTION;
  }

  if (isTiersVolumeProductVariant(variant)) {
    return USAGE_PRICING_OPTIONS.VOLUME;
  }

  if (isTiersProductVariant(variant)) {
    return USAGE_PRICING_OPTIONS.TIERS;
  }

  return raise('Unknown usage pricing model');
}

export function getAllowedUsagePricingOptions(
  variants: UsageProductVariantInput[] | undefined,
  currentVariantIndex: number,
) {
  if (isEmptyArray(variants) || isSingleItem(variants)) {
    return values(USAGE_PRICING_OPTIONS);
  }

  const otherVariants = variants.filter((_, index) => index !== currentVariantIndex);
  const otherOptions = otherVariants.map(getUsagePricingModelValue);
  const uniqueOptions = uniq(otherOptions);

  if (areTierFamilyOptions(uniqueOptions)) {
    return [USAGE_PRICING_OPTIONS.TIERS, USAGE_PRICING_OPTIONS.REVENUE_SHARE, USAGE_PRICING_OPTIONS.VOLUME];
  }

  return uniqueOptions;
}

function areTierFamilyOptions(options: UsagePricingOption[]) {
  const tierFamily = new Set<UsagePricingOption>([
    USAGE_PRICING_OPTIONS.TIERS,
    USAGE_PRICING_OPTIONS.VOLUME,
    USAGE_PRICING_OPTIONS.REVENUE_SHARE,
  ]);

  return options.every((option) => tierFamily.has(option));
}
